import { brand, showFullName } from "../lib/brand";
import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { useAuth } from "../auth";
import type { EventListItemDTO } from "../../shared/dto";
import { formatEventDate } from "../lib/format";
import {
  SectionHeading,
  StatusBadge,
  Loading,
  EmptyState,
} from "../components/ui";

// 会員ステータスの見た目マップ (MyPage と同じトーン指針)
const MEMBER_STATUS_LABELS: Record<string, string> = {
  active: "有効",
  payment_failed: "決済失敗",
  suspended: "停止",
  cancelled: "解約",
  pending_activation: "仮登録",
};
const MEMBER_STATUS_TONE: Record<
  string,
  "success" | "warning" | "danger" | "neutral"
> = {
  active: "success",
  payment_failed: "danger",
  suspended: "warning",
  cancelled: "neutral",
  pending_activation: "warning",
};

export function HomePage() {
  const { me } = useAuth();
  const [events, setEvents] = useState<EventListItemDTO[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ items: EventListItemDTO[] }>("/events?status=active&perPage=20")
      .then((r) => {
        if (!cancelled) setEvents(r.items);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // 直近5日の予定 (全公開イベント、開始が今〜5日以内)
  const next5Days = useMemo(() => {
    const now = Date.now();
    const end = now + 5 * 24 * 60 * 60 * 1000;
    return events
      .filter((e) => {
        if (!e.eventStartAt) return false;
        const t = new Date(e.eventStartAt).getTime();
        return t >= now && t <= end;
      })
      .sort(
        (x, y) =>
          new Date(x.eventStartAt!).getTime() -
          new Date(y.eventStartAt!).getTime(),
      );
  }, [events]);

  const member = me?.member ?? null;
  const hasMember = Boolean(member);

  return (
    <div className="space-y-10">
      {/* ヒーローバナー (CFL 公式バナー画像) */}
      <section className="-mx-4 relative z-10">
        <img
          src="/hero-banner.png"
          alt={`${brand.name}${showFullName ? ` (${brand.shortName})` : ""} — ${brand.tagline}`}
          className="w-full h-auto block md:rounded-2xl shadow-xl"
        />
        {hasMember && (
          <div className="mx-4 -mt-5 relative z-10">
            <div className="inline-flex items-center gap-3 rounded-xl bg-white border border-black/10 px-5 py-3.5 shadow-md">
              <span className="text-xs text-tora-ink/50">会員番号</span>
              <span className="text-lg md:text-xl font-bold font-mono tracking-wider text-tora-ink">
                {member!.memberNo}
              </span>
              <StatusBadge
                tone={MEMBER_STATUS_TONE[member!.status] ?? "neutral"}
                bold
              >
                {MEMBER_STATUS_LABELS[member!.status] ?? member!.status}
              </StatusBadge>
            </div>
          </div>
        )}
      </section>

      {/* 直近5日の予定 (全幅) */}
      <div>
        <section className="card p-6 md:p-7">
          <div className="flex items-center gap-2 mb-5">
            <span className="text-2xl" aria-hidden="true">🗓</span>
            <h3 className="text-xl md:text-2xl font-bold text-tora">直近5日の予定</h3>
            <Link
              to="/events/mine"
              className="tap-target ml-auto text-sm text-tora font-semibold hover:underline"
            >
              参加予定 &gt;
            </Link>
          </div>
          {loading ? (
            <Loading />
          ) : next5Days.length === 0 ? (
            <EmptyState icon="🗓" title="直近5日の予定はありません" />
          ) : (
            <div className="divide-y divide-black/5">
              {next5Days.map((e) => {
                const { date, time } = formatEventDate(e.eventStartAt!);
                return (
                  <Link
                    key={e.id}
                    to={`/events/${e.id}`}
                    className="flex items-start gap-3 py-3 hover:bg-black/[0.02] transition-colors"
                  >
                    <div className="shrink-0 w-16 text-center">
                      <div className="text-xs text-tora-ink/50">{date}</div>
                      <div className="text-sm font-bold text-tora tabular-nums">
                        {time}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-tora-ink leading-snug">
                        {e.title}
                      </p>
                      {e.venueName && (
                        <p className="text-xs text-tora-ink/60 mt-0.5">
                          📍 {e.venueName}
                        </p>
                      )}
                      {e.isAppliedByMe && (
                        <StatusBadge tone="accent" className="mt-1">
                          申込済み
                        </StatusBadge>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
          <Link
            to="/events"
            className="mt-6 flex items-center justify-center gap-2 rounded-xl border-2 border-tora text-tora font-bold text-base py-3.5 hover:bg-tora/5 transition-colors"
          >
            イベント一覧へ <span className="text-lg">&gt;</span>
          </Link>
        </section>
      </div>
    </div>
  );
}
